import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

const W = 600;
const H = 340;

const HUB = { cx: 410, cy: 180, label: 'Delhi, IN' };

const DESTINATIONS = [
  { code: 'us', cx: 170, cy: 158, label: 'US' },
  { code: 'ca', cx: 132, cy: 120, label: 'CA' },
  { code: 'gb', cx: 302, cy: 108, label: 'UK' },
  { code: 'de', cx: 338, cy: 120, label: 'DE' },
  { code: 'ae', cx: 378, cy: 198, label: 'UAE' },
  { code: 'cn', cx: 500, cy: 142, label: 'CN' },
  { code: 'jp', cx: 552, cy: 122, label: 'JP' },
  { code: 'my', cx: 488, cy: 240, label: 'MY' },
  { code: 'sg', cx: 520, cy: 265, label: 'SG' },
  { code: 'au', cx: 560, cy: 282, label: 'AU' }
];

const DOT_REGIONS = [
  { x: 80, y: 104, r: 48 },
  { x: 150, y: 158, r: 54 },
  { x: 110, y: 215, r: 28 },
  { x: 188, y: 245, r: 42 },
  { x: 205, y: 295, r: 22 },
  { x: 302, y: 112, r: 34 },
  { x: 340, y: 122, r: 22 },
  { x: 340, y: 200, r: 50 },
  { x: 365, y: 248, r: 28 },
  { x: 430, y: 150, r: 64 },
  { x: 496, y: 168, r: 42 },
  { x: 548, y: 122, r: 22 },
  { x: 495, y: 232, r: 30 },
  { x: 520, y: 262, r: 20 },
  { x: 556, y: 280, r: 32 }
];

const DOTS = (() => {
  const out = [];
  for (let x = 16; x < W - 16; x += 9) {
    for (let y = 44; y < H - 28; y += 9) {
      const inside = DOT_REGIONS.some(({ x: rx, y: ry, r }) => {
        const dx = x - rx;
        const dy = y - ry;
        return dx * dx + dy * dy <= r * r;
      });
      if (inside) out.push({ x, y });
    }
  }
  return out;
})();

const bezierPoints = (p0, p1, p2, steps = 40) => {
  const pts = [];
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const mt = 1 - t;
    pts.push({
      x: mt * mt * p0.x + 2 * mt * t * p1.x + t * t * p2.x,
      y: mt * mt * p0.y + 2 * mt * t * p1.y + t * t * p2.y
    });
  }
  return pts;
};

const GlobalConnectivityGlobe = () => {
  const arcs = useMemo(
    () =>
      DESTINATIONS.map((dest) => {
        const p0 = { x: HUB.cx, y: HUB.cy };
        const p2 = { x: dest.cx, y: dest.cy };
        const northward = dest.cy < HUB.cy;
        const bulge = northward ? -56 : 56;
        const p1 = { x: (p0.x + p2.x) / 2, y: (p0.y + p2.y) / 2 + bulge };
        const d = `M ${p0.x} ${p0.y} Q ${p1.x} ${p1.y} ${p2.x} ${p2.y}`;
        const pts = bezierPoints(p0, p1, p2);
        return { dest, d, pts };
      }),
    []
  );

  return (
    <div className="relative aspect-[30/17] w-full overflow-hidden rounded-2xl border border-gray-100 bg-gradient-to-br from-[#0f172a] via-[#111827] to-[#0b1220] shadow-inner">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_75%_20%,rgba(30, 64, 175,0.25),transparent_55%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_80%,rgba(59, 130, 246,0.18),transparent_60%)]" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.12]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(59, 130, 246,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246,0.6) 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        className="absolute left-4 top-4 z-10 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[11px] font-semibold text-blue-100 backdrop-blur-md"
      >
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500 opacity-80" />
          <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500" />
        </span>
        Live network
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45, duration: 0.5 }}
        className="absolute right-4 top-4 z-10 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[11px] font-semibold text-white/90 backdrop-blur-md"
      >
        612 shipments · in-transit
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6, duration: 0.5 }}
        className="absolute bottom-4 right-4 z-10 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[11px] font-semibold text-white/90 backdrop-blur-md"
      >
        On-time <span className="text-green-400">98.3%</span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.75, duration: 0.5 }}
        className="absolute bottom-4 left-4 z-10 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[11px] font-semibold text-white/90 backdrop-blur-md"
      >
        10 corridors · active
      </motion.div>

      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="relative h-full w-full"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="arcGrad" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#93c5fd" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#1d4ed8" stopOpacity="0.95" />
          </linearGradient>
          <radialGradient id="hubGlow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#1d4ed8" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#1d4ed8" stopOpacity="0" />
          </radialGradient>
          <filter id="softBlur" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="1.4" />
          </filter>
        </defs>

        <g>
          {DOTS.map((d, i) => (
            <circle key={`dot-${i}`} cx={d.x} cy={d.y} r="1.15" fill="rgba(226,232,240,0.45)" />
          ))}
        </g>

        {arcs.map(({ dest, d }, i) => (
          <g key={`arc-${dest.code}`}>
            <path d={d} fill="none" stroke="rgba(29, 78, 216,0.2)" strokeWidth="3.2" strokeLinecap="round" />
            <motion.path
              d={d}
              fill="none"
              stroke="url(#arcGrad)"
              strokeWidth="1.5"
              strokeDasharray="5 6"
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{ duration: 1.2, delay: 0.5 + i * 0.18, ease: 'easeInOut' }}
            />
          </g>
        ))}

        {DESTINATIONS.map((dest, i) => (
          <motion.g
            key={`node-${dest.code}`}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 1.1 + i * 0.1, duration: 0.45, ease: [0.34, 1.56, 0.64, 1] }}
            style={{ transformOrigin: `${dest.cx}px ${dest.cy}px` }}
          >
            <circle cx={dest.cx} cy={dest.cy} r="12" fill="rgba(29, 78, 216,0.14)" filter="url(#softBlur)" />
            <circle cx={dest.cx} cy={dest.cy} r="5.5" fill="#fff" />
            <circle cx={dest.cx} cy={dest.cy} r="2.8" fill="#1d4ed8" />
          </motion.g>
        ))}

        <g>
          <circle cx={HUB.cx} cy={HUB.cy} r="34" fill="url(#hubGlow)" />
          {[0, 1.1].map((delay, i) => (
            <motion.circle
              key={`ring-${i}`}
              cx={HUB.cx}
              cy={HUB.cy}
              r="14"
              fill="none"
              stroke="rgba(29, 78, 216,0.55)"
              strokeWidth="1.3"
              initial={{ scale: 0.6, opacity: 0.85 }}
              animate={{ scale: 2.5, opacity: 0 }}
              transition={{ duration: 2.2, repeat: Infinity, ease: 'easeOut', delay }}
              style={{ transformOrigin: `${HUB.cx}px ${HUB.cy}px` }}
            />
          ))}
          <circle cx={HUB.cx} cy={HUB.cy} r="8" fill="#1d4ed8" />
          <circle cx={HUB.cx} cy={HUB.cy} r="3.2" fill="#fff" />
        </g>

        {arcs.map(({ dest, pts }, i) => (
          <g key={`flight-${dest.code}`}>
            <motion.circle
              r="6"
              fill="#93c5fd"
              fillOpacity="0.35"
              animate={{ cx: pts.map((p) => p.x), cy: pts.map((p) => p.y) }}
              transition={{
                duration: 4.8 + i * 0.35,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1.4 + i * 0.5
              }}
            />
            <motion.circle
              r="2.8"
              fill="#eff6ff"
              animate={{ cx: pts.map((p) => p.x), cy: pts.map((p) => p.y) }}
              transition={{
                duration: 4.8 + i * 0.35,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 1.4 + i * 0.5
              }}
            />
          </g>
        ))}
      </svg>

      {DESTINATIONS.map((dest, i) => (
        <motion.div
          key={`flag-${dest.code}`}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3 + i * 0.1, duration: 0.4 }}
          className="absolute z-10 flex -translate-x-1/2 -translate-y-full items-center gap-1.5 rounded-md border border-white/15 bg-white/10 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-sm backdrop-blur-md"
          style={{
            left: `${(dest.cx / W) * 100}%`,
            top: `calc(${(dest.cy / H) * 100}% - 14px)`
          }}
        >
          <img
            src={`https://flagcdn.com/w40/${dest.code}.png`}
            srcSet={`https://flagcdn.com/w80/${dest.code}.png 2x`}
            alt=""
            className="h-2.5 w-4 rounded-[2px] object-cover"
          />
          {dest.label}
        </motion.div>
      ))}

      <motion.div
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2, duration: 0.4 }}
        className="absolute z-10 -translate-x-1/2 rounded-md border border-blue-500/40 bg-blue-700/15 px-1.5 py-0.5 text-[10px] font-semibold text-blue-100 shadow-sm backdrop-blur-md"
        style={{
          left: `${(HUB.cx / W) * 100}%`,
          top: `calc(${(HUB.cy / H) * 100}% + 16px)`
        }}
      >
        IN · Delhi hub
      </motion.div>
    </div>
  );
};

export default GlobalConnectivityGlobe;
